import type { Feature, LineString, Point } from 'geojson';
import type * as mapboxgl from 'mapbox-gl';

import type { IncidentSummary } from '../api/incidentLog';
import { incidentTitle, statusColor, statusLabel } from './incidentRekindle';

/**
 * GeoJSON for the Incidents map layer. One source carries three feature
 * families, told apart by `feature_kind`:
 *
 * - `hull`   the convex hull of each incident's detection footprint (any
 *            status, closed ones included — the map draws history);
 * - `link`   a dashed line from a closed incident to the rekindle that
 *            reopened it, so "silent rekindle of #109" is a thing you can see;
 * - `marker` the numbered "#id" disc at the footprint centre.
 *
 * Every feature carries `status`, its colour and `selected`, so the styles
 * (incidentStyles.ts) are pure expressions over properties.
 */

export interface IncidentFeatureProps {
    feature_kind: 'hull' | 'link' | 'marker';
    incident_id: number;
    status: IncidentSummary['status'];
    color: string;
    closed: boolean;
    selected: boolean;
    label?: string;
    hoverLabel?: string;
}

const isClosed = (incident: IncidentSummary) => incident.closed_at !== null;

const baseProps = (
    incident: IncidentSummary,
    selectedIds: Set<number>
): Omit<IncidentFeatureProps, 'feature_kind'> => ({
    incident_id: incident.id,
    status: incident.status,
    color: statusColor(incident.status),
    closed: isClosed(incident),
    selected: selectedIds.has(incident.id),
    hoverLabel: `#${incident.id} · ${statusLabel(incident.status)} · ${incident.headline}`,
});

export const incidentHullFeatures = (
    incidents: IncidentSummary[],
    selectedIds: Set<number>
): Feature[] =>
    incidents
        .filter((i) => i.hull && i.hull.type !== 'Point')
        .map((incident) => ({
            type: 'Feature' as const,
            id: `incident-hull-${incident.id}`,
            geometry: incident.hull,
            properties: { feature_kind: 'hull', ...baseProps(incident, selectedIds) },
        }));

export const incidentMarkerFeatures = (
    incidents: IncidentSummary[],
    selectedIds: Set<number>
): Feature<Point>[] =>
    incidents
        .filter((i) => i.centroid)
        .map((incident) => ({
            type: 'Feature' as const,
            id: `incident-marker-${incident.id}`,
            geometry: { type: 'Point' as const, coordinates: incident.centroid },
            properties: {
                feature_kind: 'marker',
                label: `#${incident.id}`,
                ...baseProps(incident, selectedIds),
                hoverLabel: `#${incident.id} ${incidentTitle(incident)} · ${statusLabel(
                    incident.status
                )} · ${incident.headline} · click for details`,
            },
        }));

/** Predecessor → rekindle, for every pair the list holds both ends of. */
export const incidentLinkFeatures = (
    incidents: IncidentSummary[],
    selectedIds: Set<number>
): Feature<LineString>[] => {
    const byId = new Map(incidents.map((i) => [i.id, i]));
    const features: Feature<LineString>[] = [];
    for (const incident of incidents) {
        if (incident.predecessor_id === null) continue;
        const predecessor = byId.get(incident.predecessor_id);
        if (!predecessor?.centroid || !incident.centroid) continue;
        features.push({
            type: 'Feature',
            id: `incident-link-${predecessor.id}-${incident.id}`,
            geometry: {
                type: 'LineString',
                coordinates: [predecessor.centroid, incident.centroid],
            },
            properties: {
                feature_kind: 'link',
                ...baseProps(incident, selectedIds),
                selected: selectedIds.has(incident.id) || selectedIds.has(predecessor.id),
                hoverLabel: `#${incident.id} rekindled #${predecessor.id}`,
            },
        });
    }
    return features;
};

const extendBounds = (bounds: mapboxgl.LngLatBoundsLike | null, geometry: GeoJSON.Geometry) => {
    const coords: number[][] = [];
    const walk = (c: unknown) => {
        if (!Array.isArray(c)) return;
        if (typeof c[0] === 'number') coords.push(c as number[]);
        else c.forEach(walk);
    };
    walk((geometry as { coordinates?: unknown }).coordinates);
    let [[minLon, minLat], [maxLon, maxLat]] = (bounds as [[number, number], [number, number]]) ?? [
        [Infinity, Infinity],
        [-Infinity, -Infinity],
    ];
    for (const [lon, lat] of coords) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
    }
    return [
        [minLon, minLat],
        [maxLon, maxLat],
    ] as [[number, number], [number, number]];
};

/**
 * Bounds enclosing an incident and its whole rekindle lineage (predecessor
 * chain + rekindles) among *incidents* — what "zoom to" fits. Null when
 * nothing has geometry.
 */
export const incidentLineageBounds = (
    incident: IncidentSummary,
    incidents: IncidentSummary[]
): [[number, number], [number, number]] | null => {
    const byId = new Map(incidents.map((i) => [i.id, i]));
    const members = [incident, ...incident.chain.map((id) => byId.get(id)), ...incident.rekindles.map((id) => byId.get(id))].filter(
        (i): i is IncidentSummary => !!i
    );
    let bounds: [[number, number], [number, number]] | null = null;
    for (const member of members) {
        if (member.hull) bounds = extendBounds(bounds, member.hull);
        else if (member.centroid)
            bounds = extendBounds(bounds, { type: 'Point', coordinates: member.centroid });
    }
    if (!bounds || !Number.isFinite(bounds[0][0])) return null;
    // A single point or a tiny hull gives zero-area bounds; pad so fitBounds
    // lands at a sensible zoom rather than the maximum.
    const pad = 0.01;
    if (bounds[1][0] - bounds[0][0] < pad || bounds[1][1] - bounds[0][1] < pad) {
        bounds = [
            [bounds[0][0] - pad, bounds[0][1] - pad],
            [bounds[1][0] + pad, bounds[1][1] + pad],
        ];
    }
    return bounds;
};
